import {
  JobState,
  JobStateEvent,
  OrderUnitOfMeasure,
  PricingResolution,
  RateType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil, uniq } from 'lodash-es';

import { isLegacyCommissionRateType } from '~components/PayCommission/commissionPercentageSchema';
import { DriverDay } from '~hooks/useDriverDays/models';
import { Invoice } from '~hooks/useInvoices/models';
import { Job, JobSummary } from '~hooks/useJob/models';
import { Load, LoadDTO } from '~hooks/useLoad/models';
import { DeliveredQuantity } from '~hooks/useOrders/models';
import UserStore from '~store/UserStore';
import { Nullable } from '~types/Nullable';
import { toRateNumber } from '~utils/rateFormatting';

/**
 * Determines if an already sent job can be accepted
 * @param job Job instance
 * @returns `true` if the sent job is acceptable, `false` otherwhise
 */
export const isSentJobAcceptable = (job: Job) => {
  const { status, driver, vendorJobAssignment, editable } = job;

  const isSent = status === JobState.SENT;
  const isNotAssigned = !driver && !vendorJobAssignment;

  return editable && isSent && isNotAssigned;
};

/**
 * The subset of an order's jobs this company can accept right now — jobs still
 * awaiting its Accept/Reject response (see isSentJobAcceptable). Single source of
 * truth for both the Accept-All CTA (which bulk-accepts these jobs) and the
 * External-tab accent (which highlights orders that have any), so the accent
 * appears exactly when the Accept-All button does.
 * @param jobs an order's jobs
 * @returns the acceptable jobs
 */
export const getAcceptableJobs = (jobs: Job[]) => jobs.filter(isSentJobAcceptable);

/**
 * Determines if a job can be sent to an assignee
 * @param job Job instance
 * @returns `true` if the job can be sent to an assignee, `false` otherwhise
 */
export const isJobSendable = (job: Job) => {
  return job.stateEvents.includes(JobStateEvent.REQUEST);
};

// A driverless job 422s with the same `job_id` error an existing conversation gives, and the
// conversation opens on the Messages page, which a viewer with messaging off does not have.
export const canMessageJobDriver = (
  job: Pick<Job, 'driver' | 'messagingEnabled'>,
  isInAppMessagingEnabled: boolean,
) => isInAppMessagingEnabled && job.messagingEnabled && Boolean(job.driver);

export const isJobCopyable = (job: Job) =>
  Boolean(job.driver) || Boolean(job.vendorJobAssignment?.vendorAccount);

/**
 * Determines if a job can be assigned, including whether the acting company is entitled
 * to dispatch it at all.
 * @param job Job instance
 * @returns `true` if the job can be assigned, `false` otherwhise
 */
export const isJobAssignable = (job: Job) => {
  const hasDriver = Boolean(job.driver);
  const hasVendor = Boolean(job.vendorJobAssignment?.vendorAccount);
  const isAssigned = hasDriver || hasVendor;

  const isAcceptable = isSentJobAcceptable(job);
  const isAssignable = job.driverOrVendorAssignable && !isAcceptable && !isAssigned;

  return isAssignable;
};

export function extractJobDeliveredQuantitiesFromJobSummary(object: Job | Invoice) {
  const { jobSummary } = object;

  if (!jobSummary || !jobSummary.deliveredQuantities[0]) {
    return null;
  }

  const entry = jobSummary.deliveredQuantities.find((item) => {
    return item.materialName === object.material?.name;
  });

  if (entry) {
    return {
      delivered: Number(entry.delivered),
      total: Number(entry.total),
      unitOfMeasure: entry.unitOfMeasure,
    };
  }

  return {
    delivered: Number(jobSummary.deliveredQuantities[0].delivered),
    total: Number(jobSummary.deliveredQuantities[0].total),
    unitOfMeasure: jobSummary.deliveredQuantities[0].unitOfMeasure,
  };
}

export function getUnambiguousDeliveredUnitOfMeasure(
  jobSummary: Nullable<JobSummary>,
  materialName: string | null,
) {
  const unitsOfMeasure = uniq(
    (jobSummary?.deliveredQuantities ?? [])
      .filter((deliveredQuantity) => deliveredQuantity.materialName === materialName)
      .map(({ unitOfMeasure }) => unitOfMeasure),
  );

  return unitsOfMeasure.length === 1 ? (unitsOfMeasure[0] ?? null) : null;
}

export function getLargestDeliveredQuantitySum(
  deliveredQuantities: Array<DeliveredQuantity>,
): { total: number; unitOfMeasure: OrderUnitOfMeasure | null } {
  const sumsByUoM = deliveredQuantities.reduce(
    (obj, item) => {
      const uom = String(item.unitOfMeasure);

      obj[uom] = (obj[uom] ?? 0) + Number(item.delivered);

      return obj;
    },
    {} as Record<string, number>,
  );
  const [largestSumUoM, largestSumTotal] = Object.entries(sumsByUoM).sort(
    ([, aSum], [, bSum]) => {
      return bSum - aSum;
    },
  )[0] ?? [null, 0];

  return {
    total: Number(largestSumTotal),
    unitOfMeasure: (largestSumUoM as OrderUnitOfMeasure) ?? null,
  };
}

export function extractRateInfoFromJob(
  job: Job,
  target: 'customer' | 'haul' | 'pay' | 'driver' | 'hauler',
): { rateType: RateType; rateValue: number | null } | null {
  if (target === 'driver') {
    const { driverRate, driverRateType, driverRateValue } = job;

    if (driverRateType) {
      return { rateType: driverRateType, rateValue: driverRateValue ?? null };
    }

    if (
      driverRate &&
      (driverRate.rate > 0 || driverRate.type === RateType.RATE_FLAT_COMMISSION)
    ) {
      return { rateType: driverRate.type as RateType, rateValue: driverRate.rate };
    }

    return null;
  }

  if (target === 'haul' || target === 'customer') {
    const { rate, rateType, rateValue } = job.customerJobAssignment ?? {};

    if (rateType) {
      return { rateType: rateType, rateValue: rateValue ?? null };
    }

    if (rate && (rate.rate > 0 || rate.type === RateType.RATE_FLAT_COMMISSION)) {
      return { rateType: rate.type as RateType, rateValue: rate.rate };
    }

    return null;
  }

  if (target === 'hauler') {
    if (job.haulerRateType) {
      return { rateType: job.haulerRateType, rateValue: null };
    }

    return null;
  }

  const { rate, rateType, rateValue } = job.vendorJobAssignment ?? {};

  if (rateType) {
    return { rateType: rateType, rateValue: rateValue ?? null };
  }

  if (rate && (rate.rate > 0 || rate.type === RateType.RATE_FLAT_COMMISSION)) {
    return { rateType: rate.type as RateType, rateValue: rate.rate };
  }

  return null;
}

export type PayCommissionParty = 'vendor' | 'driver';

export function jobPayCommission(job: Job, party: PayCommissionParty) {
  if (party === 'driver') {
    return {
      inheritedPercentage: job.inheritedDriverPayCommissionPercentage,
      locked: job.driverCommissionLocked,
      netRate: job.netDriverRate,
      percentage: job.driverPayCommissionPercentage,
      resolution: job.driverCommissionResolution,
    };
  }

  return {
    inheritedPercentage:
      job.vendorJobAssignment?.inheritedVendorPayCommissionPercentage ?? null,
    locked: job.vendorJobAssignment?.vendorCommissionLocked ?? null,
    netRate: job.vendorJobAssignment?.netVendorRate ?? null,
    percentage: job.vendorJobAssignment?.vendorPayCommissionPercentage ?? null,
    resolution: job.vendorJobAssignment?.vendorCommissionResolution ?? null,
  };
}

export type JobPayCommission = ReturnType<typeof jobPayCommission>;

export function invoicePayCommission(
  invoice: Invoice,
  party: PayCommissionParty,
): JobPayCommission {
  if (party === 'driver') {
    return {
      inheritedPercentage: invoice.inheritedDriverPayCommissionPercentage,
      locked: invoice.driverCommissionLocked,
      netRate: invoice.netDriverRate,
      percentage: invoice.driverPayCommissionPercentage,
      resolution: invoice.driverCommissionResolution,
    };
  }

  return {
    inheritedPercentage: invoice.inheritedVendorPayCommissionPercentage,
    locked: invoice.vendorCommissionLocked,
    netRate: invoice.netVendorRate,
    percentage: invoice.vendorPayCommissionPercentage,
    resolution: invoice.vendorCommissionResolution,
  };
}

export function savedPayCommissionOverride(commission: JobPayCommission | undefined) {
  return commission?.resolution === PricingResolution.MANUAL
    ? commission.percentage
    : null;
}

export function payCommissionPatch(
  party: PayCommissionParty,
  percentage: string | null | undefined,
) {
  if (percentage === undefined) {
    return {};
  }

  return party === 'driver'
    ? { driver_pay_commission_percentage: percentage }
    : { vendor_pay_commission_percentage: percentage };
}

interface GrossRate {
  rateId: string | null | undefined;
  rateType: string | null | undefined;
  rateValue: unknown;
}

/**
 * The submit decision a rate editor shares with every other rate editor: whether
 * to send the gross rate fields at all, and which pay-commission percentage goes
 * with them. Switching to a legacy commission rate type clears a saved override,
 * because the two ways of expressing a commission cannot both hold.
 */
export function resolveRateSubmit({
  grossUnchanged,
  rateType,
  savedPercentage,
  submittedPercentage,
}: {
  grossUnchanged: boolean;
  rateType: string | null | undefined;
  savedPercentage: string | null | undefined;
  submittedPercentage: string | null | undefined;
}) {
  const commissionChanged = submittedPercentage !== savedPercentage;
  const clearsSavedCommission =
    isLegacyCommissionRateType(rateType) && !isNil(savedPercentage);

  let payCommissionPercentage: string | null | undefined;
  if (clearsSavedCommission) {
    payCommissionPercentage = null;
  } else if (commissionChanged) {
    payCommissionPercentage = submittedPercentage;
  }

  return {
    commissionChanged,
    nothingChanged: grossUnchanged && !commissionChanged,
    omitGrossRate: grossUnchanged,
    payCommissionPercentage,
  };
}

export function isGrossRateUnchanged(current: GrossRate, saved: GrossRate) {
  return (
    (current.rateId ?? null) === (saved.rateId ?? null) &&
    (current.rateType ?? null) === (saved.rateType ?? null) &&
    toRateNumber(current.rateValue) === toRateNumber(saved.rateValue)
  );
}

/**
 * Which rate sides a job exposes to a rate editor: the customer side, plus the
 * single assignee side — the vendor's on a vendor-assigned job, the driver's
 * otherwise.
 */
export function resolveJobRateSides(job: Job) {
  const isAssignedToVendor = Boolean(job.vendorJobAssignment?.id);
  const isAssignedToDriver = !isAssignedToVendor && Boolean(job.driver?.id);

  return {
    hasCustomerRateSide: !job.haulerRateType && Boolean(job.customerJobAssignment),
    hasDriverRateSide: isAssignedToDriver,
    hasVendorRateSide: Boolean(job.vendorJobAssignment),
    isAssignedToDriver,
    isAssignedToVendor,
  };
}

/**
 * Narrows the job's three per-billing-side rate locks to the two sides a rate
 * form renders: the customer rate, and the single assignee rate — the vendor's
 * on a vendor-assigned job, the driver's otherwise.
 */
export function resolveRateEditableSides(job: Job, isAssignedToVendor: boolean) {
  const { customer, vendor, driver } = job.rateEditable;

  return { customer, assignee: isAssignedToVendor ? vendor : driver };
}

function describeCustomerRateLock(isOrderOwner: boolean, isRateEditable: boolean) {
  if (!isOrderOwner) return t('dispatch.job.rates.locked_for_non_order_owner');
  if (!isRateEditable) return t('dispatch.job.rates.locked_by_approved_invoice');

  return undefined;
}

/**
 * The hover text explaining why each rate side is read-only, or `undefined`
 * while it is still editable. A disabled control is otherwise silent about its
 * own cause, and the two causes are not interchangeable: the customer rate is
 * off-limits to sub-vendors and drivers down the dispatch chain regardless of
 * invoice state, and either side locks once its own invoice is approved.
 */
export function resolveRateDisabledReasons(
  rateEditableSides: ReturnType<typeof resolveRateEditableSides>,
  isOrderOwner: boolean,
) {
  return {
    customer: describeCustomerRateLock(isOrderOwner, rateEditableSides.customer),
    assignee: rateEditableSides.assignee
      ? undefined
      : t('dispatch.job.rates.locked_by_approved_invoice'),
  };
}

interface ApprovalStatus {
  isApproved: boolean;
  isUnapprovable: boolean;
  isApprovable: boolean;
}

/**
 * Determines the approval status of an object (Job, LoadDTO, or Load) based on user permissions
 * @param object Job, LoadDTO, or Load instance to evaluate
 * @param userPermissions User permissions object from UserStore
 * @param isOrderEditable Whether the associated order is editable
 * @returns Object containing approval status flags:
 *   - `isApproved`: true if the object is approved based on user's permission level
 *   - `isUnapprovable`: true if the object can be unapproved (requires editable order and approved status)
 *   - `isApprovable`: true if the object can be approved (requires editable order and unapproved status)
 */
export const getApprovalStatus = (
  object: Job | LoadDTO | Load | DriverDay,
  userPermissions: ReturnType<UserStore['getPermissions']>,
  isOrderEditable: boolean,
): ApprovalStatus => {
  let isApproved = false;

  if (userPermissions.canBillingApprove) {
    isApproved = !!object.billingApprovedAt;
  } else if (userPermissions.canFieldApprove) {
    isApproved = !!object.fieldApprovedAt;
  }

  const hasApprovalPermission =
    userPermissions.canBillingApprove || userPermissions.canFieldApprove;
  const isUnapprovable = Boolean(
    object.unapprovable && isOrderEditable && hasApprovalPermission && isApproved,
  );
  const isApprovable = Boolean(
    object.approvable && isOrderEditable && hasApprovalPermission && !isApproved,
  );

  return {
    isApproved,
    isUnapprovable,
    isApprovable,
  };
};

/**
 * Determines if a job can be deleted based on its status
 * @param job Job instance
 * @returns `true` if the job can be deleted, `false` otherwise
 */
export function isJobDeletable(job: Job): boolean {
  return (
    job.deletable &&
    (job.status === JobState.CANCELED ||
      job.status === JobState.CREATED ||
      job.status === JobState.COMPLETED ||
      job.status === JobState.ENDED)
  );
}
